// src/utils/vesselHelper.jsx

import L from "leaflet"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import NavigationIcon from "@mui/icons-material/Navigation"
import ReactDOMServer from "react-dom/server"
import { fromJS } from "immutable"
import RouteHelper, { findPlannedStateIconColor } from "./routeHelper"
import { nowUtc, nowMs } from "./appNow"
import { RouteAnalyser } from "@/features/routeAnalysis/RouteAnalyser2"
import { getNormFromMap, adjustLonToNorm } from "@/utils/viewportHelper"
dayjs.extend(utc)

/**
 * Base pixel size for the vessel icon (MUI <NavigationIcon />).
 * @type {number}
 */
const ICON_SIZE = 30

// Below this SOG a vessel isn't making way; its heading is noise → render circle.
const MIN_MOVING_SOG_KN = 2

// Known SOG below threshold → circle; valid heading → rotate; else null (keep computed bearing).
export function resolveSensorRotation({ heading, sog }) {
  const sogNum = sog == null ? NaN : Number(sog)
  if (Number.isFinite(sogNum) && sogNum < MIN_MOVING_SOG_KN) return { circle: true }
  const h = heading == null ? NaN : Number(heading)
  if (!Number.isFinite(h)) return null
  return { rotationDeg: ((h % 360) + 360) % 360 }
}

function sourceWaypointForVessel(features, interpolateData) {
  const match = interpolateData?.intervaltree_match
  if (match === "before_route") return features[0]
  const idx = interpolateData?.intervaltree_index
  if (match === "on_route" && Number.isInteger(idx) && features[idx]) return features[idx]
  return features[features.length - 1]
}

// Reported only: computed bearing → course_bearing, icon rotated by the sensor heading of the waypoint the vessel sits on.
function applyReportedSensorRotation(vesselPosition, routeFeatures) {
  if (vesselPosition.routeType !== "reported") return
  const feature = vesselPosition.geojson.features[0]
  if (!feature) return
  const analyse = feature.properties.analyse
  if (analyse?.rhumbline_bearing != null && analyse.course_bearing === undefined) {
    analyse.course_bearing = analyse.rhumbline_bearing
  }
  const src = sourceWaypointForVessel(routeFeatures, analyse?.interpolate_data)?.properties ?? {}
  const heading = src.heading ?? src.albis_sensors?.headingTrue_deg ?? null
  const sog = src.sog ?? src.albis_sensors?.speedOverGround_kn ?? null
  // Card reads these, so it shows the same values the icon was rotated by.
  feature.properties.heading = heading
  feature.properties.sog = sog
  const rotation = resolveSensorRotation({ heading, sog })
  if (!rotation) return
  feature.properties.analyse = {
    ...analyse,
    ...(rotation.circle
      ? { is_low_distance: true }
      : { rhumbline_bearing: rotation.rotationDeg, is_low_distance: false }),
  }
}

/**
 * Anchor (in pixels) for the vessel icon. Centered so rotation pivots around the icon center.
 * @type {[number, number]}
 */
const ICON_ANCHOR = [ICON_SIZE / 2, ICON_SIZE / 2]

/**
 * Registry of Leaflet `DivIcon`s used to render vessels in different states/colors.
 * Keys should align with values returned by `RouteHelper.findRouteColor(vesselIconColor)`,
 * e.g. "blue", "green", "red", and their pulsing counterparts "blue_pulse", "green_pulse", "purple".
 *
 * Note:
 * - These icons are rendered from a React MUI `NavigationIcon` to an HTML string via `ReactDOMServer`.
 * - CSS classes like `.vessel-icon-pulse`, `.gps_ring_blue`, etc. must exist in your stylesheet.
 *
 * @type {Record<string, import('leaflet').DivIcon>}
 */
export const shipMarkerIcon = {
  blue: L.divIcon({
    className: "vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <NavigationIcon
        style={{ color: "blue", fontSize: 30, stroke: "white", strokeWidth: 1 }}
      />,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  green: L.divIcon({
    className: "vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <NavigationIcon
        style={{
          color: "green",
          fontSize: 30,
          stroke: "white",
          strokeWidth: 1,
        }}
      />,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  blue_pulse: L.divIcon({
    className: "leaflet-div-icon vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <div className="vessel-icon-pulse ">
        <NavigationIcon
          style={{
            color: "blue",
            fontSize: 30,
            stroke: "white",
            strokeWidth: 1,
          }}
        />
        <div className="gps_ring_blue"></div>
      </div>,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  green_pulse: L.divIcon({
    className: "leaflet-div-icon vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <div className="vessel-icon-pulse ">
        <NavigationIcon
          style={{
            color: "green",
            fontSize: 30,
            stroke: "white",
            strokeWidth: 1,
          }}
        />
        <div className="gps_ring_green"></div>
      </div>,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  red: L.divIcon({
    className: "vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <NavigationIcon
        style={{ color: "red", fontSize: 30, stroke: "white", strokeWidth: 1 }}
      />,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  purple: L.divIcon({
    className: "leaflet-div-icon vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <div className="vessel-icon-pulse ">
        <NavigationIcon
          style={{
            color: "#7b1fa2",
            fontSize: 30,
            stroke: "white",
            strokeWidth: 1,
          }}
        />
        <div className="gps_ring_purple"></div>
      </div>,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  purple_pulse: L.divIcon({
    className: "leaflet-div-icon vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <div className="vessel-icon-pulse ">
        <NavigationIcon
          style={{
            color: "#7b1fa2",
            fontSize: 30,
            stroke: "white",
            strokeWidth: 1,
          }}
        />
        <div className="gps_ring_purple"></div>
      </div>,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  grey: L.divIcon({
    className: "vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <NavigationIcon
        style={{ color: "#6b7480", fontSize: 30, stroke: "white", strokeWidth: 1 }}
      />,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  grey_pulse: L.divIcon({
    className: "leaflet-div-icon vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <div className="vessel-icon-pulse ">
        <NavigationIcon
          style={{
            color: "#6b7480",
            fontSize: 30,
            stroke: "white",
            strokeWidth: 1,
          }}
        />
        <div className="gps_ring_grey"></div>
      </div>,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  yellow: L.divIcon({
    className: "vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <NavigationIcon
        style={{ color: "#d4a106", fontSize: 30, stroke: "white", strokeWidth: 1 }}
      />,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
  yellow_pulse: L.divIcon({
    className: "leaflet-div-icon vessel-rotated-icon",
    html: ReactDOMServer.renderToString(
      <div className="vessel-icon-pulse ">
        <NavigationIcon
          style={{
            color: "#d4a106",
            fontSize: 30,
            stroke: "white",
            strokeWidth: 1,
          }}
        />
        <div className="gps_ring_yellow"></div>
      </div>,
    ),
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  }),
}

function _selectVesselIconKey(route, markedVesselNames) {
  let vesselIconColor
  if (
    (route.routeType !== "reported" && !RouteHelper.isPlanType(route.routeType)) ||
    markedVesselNames.length === 0
  ) {
    vesselIconColor = route.routeType
  } else if (
    markedVesselNames[markedVesselNames.length - 1].vesselName !== route.vesselName
  ) {
    vesselIconColor = route.routeType
  } else if (route.routeType === "reported") {
    vesselIconColor = "reported_pulse"
  } else if (RouteHelper.isPlanType(route.routeType)) {
    vesselIconColor = "planned_pulse"
  }
  const key = RouteHelper.findRouteColor(vesselIconColor)
  // Plan-type markers take their color from the lifecycle state.
  return _applyPlanStateIconColor(key, route)
}

// Recolor a plan-type icon key by lifecycle state, keeping the pulse suffix.
function _applyPlanStateIconColor(key, route) {
  if (!RouteHelper.isPlanType(route?.routeType)) return key
  const pulse = key.endsWith("_pulse")
  const stateColor = findPlannedStateIconColor(route?.state)
  return pulse ? `${stateColor}_pulse` : stateColor
}

function _iconKeyToColorPulse(key) {
  switch (key) {
    case "blue": return ["blue", false]
    case "blue_pulse": return ["blue", true]
    case "green": return ["green", false]
    case "green_pulse": return ["green", true]
    case "red": return ["red", false]
    case "purple": return ["purple", true]
    case "purple_pulse": return ["purple", true]
    case "grey": return ["grey", false]
    case "grey_pulse": return ["grey", true]
    case "yellow": return ["yellow", true]
    case "yellow_pulse": return ["yellow", true]
    default: return ["green", false]
  }
}

/**
 * Build a vessel divIcon with the rotation baked in as a CSS transform.
 * Replaces the legacy path via L.rotatedMarker (which was applying rotation
 * at 2× rate for unknown reasons).
 *
 * @param {object} params
 * @param {string} params.color - "blue" | "green" | "red" | "purple" (pulsing variants get a ring)
 * @param {boolean} params.pulse - true to add the pulsing-ring wrapper
 * @param {number} params.rotationDeg - clockwise degrees from north
 * @returns {import('leaflet').DivIcon}
 */
function buildRotatedVesselIcon({ color, pulse, rotationDeg }) {
  const svgColor =
    color === "grey" ? "#6b7480"
    : color === "purple" ? "#7b1fa2"
    : color === "yellow" ? "#d4a106"
    : color
  const iconHtml = ReactDOMServer.renderToString(
    <NavigationIcon
      style={{ color: svgColor, fontSize: 30, stroke: "white", strokeWidth: 1 }}
    />,
  )
  // Inner wrapper holds the rotation; transform-origin is explicit so the
  // arrow pivots around its visual center regardless of any outer transforms.
  const rotatedInner = `<div style="width:30px;height:30px;display:flex;align-items:center;justify-content:center;transform:rotate(${rotationDeg}deg);transform-origin:50% 50%;">${iconHtml}</div>`
  const ringClass =
    color === "blue" ? "gps_ring_blue"
    : color === "green" ? "gps_ring_green"
    : color === "purple" ? "gps_ring_purple"
    : color === "grey" ? "gps_ring_grey"
    : color === "yellow" ? "gps_ring_yellow"
    : null
  const html = pulse && ringClass
    ? `<div class="vessel-icon-pulse">${rotatedInner}<div class="${ringClass}"></div></div>`
    : rotatedInner
  return L.divIcon({
    className: "vessel-rotated-icon",
    html,
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: ICON_ANCHOR,
  })
}

/**
 * Get (or create) a shared SVG renderer in a dedicated pane for vessel graphics.
 * The renderer is cached on the map instance (under a private key) to be reused.
 *
 * @param {{ map: import('leaflet').Map | import('leaflet').Layer, paneName?: string, zIndex?: number }} params
 * @param {import('leaflet').Map | import('leaflet').Layer} params.map - Leaflet map, or a layer already added to a map.
 * @param {string} [params.paneName="vesselIconPane"] - Target pane name.
 * @param {number} [params.zIndex=1050] - Pane z-index (higher renders on top).
 * @returns {import('leaflet').SVG | undefined} The SVG renderer, or `undefined` if no map was available.
 */
export function getVesselSvgRenderer({
  map,
  paneName = "vesselIconPane",
  zIndex = 1050,
}) {
  const hostMap = map && "getPane" in map ? map : map?._map
  if (!hostMap) return undefined
  if (!hostMap.getPane(paneName)) {
    hostMap.createPane(paneName)
    hostMap.getPane(paneName).style.zIndex = String(zIndex)
  }
  const cacheKey = `_renderer_${paneName}`
  if (!hostMap[cacheKey]) hostMap[cacheKey] = L.svg({ pane: paneName })
  return hostMap[cacheKey]
}

/**
 * Decide which vessel icon to use based on route type and current selection.
 * The returned key is resolved via `RouteHelper.findRouteColor(...)` and looked up in `shipMarkerIcon`.
 *
 * @param {{ routeType: string, vesselName: string }} route - The vessel route descriptor.
 * @param {Array<{ vesselName: string }>} markedVesselNames - List of selected vessels; last entry is treated as active.
 * @returns {import('leaflet').Icon} A Leaflet `DivIcon` corresponding to the vessel's state.
 */
export function selectVesselIcon(route, markedVesselNames) {
  let vesselIconColor

  if (
    (route.routeType !== "reported" && !RouteHelper.isPlanType(route.routeType)) ||
    markedVesselNames.length === 0
  ) {
    vesselIconColor = route.routeType
  } else if (
    markedVesselNames[markedVesselNames.length - 1].vesselName !==
    route.vesselName
  ) {
    vesselIconColor = route.routeType
  } else if (
    route.routeType === "reported" &&
    markedVesselNames[markedVesselNames.length - 1].vesselName ===
      route.vesselName
  ) {
    vesselIconColor = "reported_pulse"
  } else if (
    RouteHelper.isPlanType(route.routeType) &&
    markedVesselNames[markedVesselNames.length - 1].vesselName ===
      route.vesselName
  ) {
    vesselIconColor = "planned_pulse"
  }

  const key = _applyPlanStateIconColor(
    RouteHelper.findRouteColor(vesselIconColor),
    route,
  )
  return shipMarkerIcon[key] || shipMarkerIcon.default
}

/**
 * Create a rotated vessel marker (requires `leaflet-rotatedmarker` plugin).
 * The marker is NOT added to any layer group automatically; use `.addTo(map)` if needed.
 *
 * @param {{
 *   vesselPoints: { vesselName: string, routeType: string, geojson: { features: Array<{ geometry: { coordinates: [number, number] }, properties: { analyse: { rhumbline_bearing: number }}}>} },
 *   markedVesselNames: Array<{ vesselName: string }>,
 *   recenterToPoint: (latlng: [number, number]) => void,
 *   setMarkedVesselNames?: (list: Array<{vesselName: string, routeType: string, lonlat: [number, number]}>) => void,
 *   map: import('leaflet').Map,
 *   handleMarkerClick: () => void
 * }} params
 * @returns {import('leaflet').Marker} A rotated Leaflet marker configured with the appropriate icon.
 */
export function renderRotatedIcon({
  vesselPoints,
  markedVesselNames,
  recenterToPoint,
  setMarkedVesselNames,
  map,
  handleMarkerClick,
}) {
  if (!map.getPane("vesselIconPane")) {
    map.createPane("vesselIconPane")
    map.getPane("vesselIconPane").style.zIndex = 1050
  }

  const last =
    vesselPoints.geojson.features[vesselPoints.geojson.features.length - 1]
  const norm = getNormFromMap(map)
  const adjLng = adjustLonToNorm(last.geometry.coordinates[0], norm)
  const latlon = { lat: last.geometry.coordinates[1], lng: adjLng }

  // Resolve color + pulse from the same key selectVesselIcon would return,
  // then build a divIcon with the rotation baked in via CSS transform.
  // This replaces L.rotatedMarker, which was applying rotation at 2× rate.
  const iconKey = _selectVesselIconKey(vesselPoints, markedVesselNames)
  const [color, pulse] = _iconKeyToColorPulse(iconKey)
  const icon = buildRotatedVesselIcon({
    color,
    pulse,
    rotationDeg: last.properties.analyse.rhumbline_bearing,
  })

  const marker = L.marker(latlon, {
    icon,
    interactive: true,
    opacity: 1,
    pane: "vesselIconPane",
  })

  marker.on("click", (e) => {
    L.DomEvent.stopPropagation(e)
    recenterToPoint([latlon.lat, latlon.lng])
    handleMarkerClick(latlon)

    // Pathfinder is a pseudo-vessel — don't replace marked-vessels list
    // (would hide all real reported routes).
    if (vesselPoints.routeType === "Pathfinder") return

    const reportedRoutesEndpoints = [
      {
        vesselName: vesselPoints.vesselName,
        vesselId: vesselPoints.vesselId ?? null,
        routeType: vesselPoints.routeType,
        lonlat: [latlon.lng, latlon.lat],
      },
    ]
    setMarkedVesselNames?.(reportedRoutesEndpoints)
  })

  return marker
}

/**
 * Create a circle marker for "low-distance" or simplified vessel positions.
 * The marker is clickable; on click it recenters the map and updates selection state.
 *
 * @param {{
 *   latlon: import('leaflet').LatLngExpression,
 *   vesselPoints: { vesselName: string, routeType: string, geojson: { features: Array<{ geometry: { coordinates: [number, number] } }> } },
 *   recenterToPoint: (latlng: [number, number]) => void,
 *   setMarkedVesselNames?: (list: Array<{vesselName: string, routeType: string, lonlat: [number, number]}>) => void,
 *   map: import('leaflet').Map,
 *   handleMarkerClick: () => void
 * }} params
 * @returns {import('leaflet').CircleMarker} A Leaflet circle marker configured for the vessel.
 */
export function renderCircleIcon({
  latlon,
  vesselPoints,
  recenterToPoint,
  setMarkedVesselNames,
  map,
  handleMarkerClick,
}) {
  if (!map.getPane("vesselIconPane")) {
    map.createPane("vesselIconPane")
    map.getPane("vesselIconPane").style.zIndex = 1100
  }
  const renderer = getVesselSvgRenderer({
    map,
    paneName: "vesselIconPane",
    zIndex: 1100,
  })

  const marker = L.circleMarker(latlon, {
    radius: 10,
    fill: true,
    fillColor: "#1973c3",
    fillOpacity: 1,
    pane: "vesselIconPane",
    interactive: true,
    renderer,
    className: "vessel-circle-marker ",
  })

  marker.on("click", (e) => {
    L.DomEvent.stopPropagation(e)
    recenterToPoint([latlon.lat, latlon.lng])
    const ll =
      latlon?.lat != null && latlon?.lng != null ? latlon : marker.getLatLng()
    handleMarkerClick(ll)

    // Pathfinder is a pseudo-vessel — don't replace marked-vessels list
    // (would hide all real reported routes).
    if (vesselPoints.routeType === "Pathfinder") return

    const reportedRoutesEndpoints = [
      {
        vesselName: vesselPoints.vesselName,
        vesselId: vesselPoints.vesselId ?? null,
        routeType: vesselPoints.routeType,
        lonlat: vesselPoints.geojson.features.at(-1).geometry.coordinates,
      },
    ]
    setMarkedVesselNames?.(reportedRoutesEndpoints)
    // setOpenDrawer?.(true)
    // setOpenVesselList?.(true)
  })

  return marker
}

/**
 * Attach a vessel popup + permanent tooltip to a feature layer.
 * Tooltip shows vessel name; popup shows timestamp, position and SOG/COG depending on data source.
 *
 * @param {{ vesselPoints: { vesselName: string, routeType: string }, feature: any, layer: import('leaflet').Layer }} params
 * @returns {void}
 */
export function applyVesselPopup({ vesselPoints, feature, layer }) {
  // Tooltip color follows lifecycle state for plan types, else the routeType.
  const labelVariant = RouteHelper.isPlanType(vesselPoints.routeType)
    ? vesselPoints.state ?? "active"
    : vesselPoints.routeType
  // Disambiguated per-route label when present, else the plain vessel name.
  const labelText = vesselPoints.label ?? vesselPoints.vesselName
  layer.bindTooltip("<div>" + labelText + "</div>", {
    permanent: true,
    direction: "center",
    opacity: 1,
    offset: L.point(0, -30),
    className: "my_tooltip_labels_vessels_" + labelVariant,
  })

  // Vessel details are displayed via React card (VesselMiniCard), not Leaflet popup.
}

/**
 * Build a one-point-per-route GeoJSON snapshot for the current time slider position.
 *
 * How it works:
 * 1) Chooses the first waypoint whose `properties.timestamp` is greater than `sliderPosition.label`;
 *    if none, falls back to the route’s last waypoint.
 * 2) If the route has at least two waypoints, it:
 *    - Creates an interval tree with `RouteAnalyser.setTimeIntervalTree(fromJS(route.points))`
 *    - Interpolates the vessel position at `sliderPosition.label` with
 *      `RouteAnalyser.setVesselInterpolatePointByTime(..., "rhumbline")`
 *    - Normalizes the effective timestamp for “before_route” / “after_route” edge cases.
 *    - Computes vessel bearing via `RouteAnalyser.setVesselBearing(..., "rhumbline")`
 * 3) For reported routes: if the interpolated feature has `albis_reports === null`,
 *    the function backfills it from the last non-null `albis_reports` found in the route.
 *
 * @param {{
 *   routes: Array<{
 *     points: {
 *       vesselName: string,
 *       routeType: "reported" | "planned" | string,
 *       geojson: {
 *         features: Array<{
 *           geometry: { coordinates: [number, number] }, // [lon, lat]
 *           properties: {
 *             timestamp?: number,
 *             albis_reports?: any,
 *             analyse?: {
 *               interpolate_data?: {
 *                 intervaltree_match?: "before_route" | "after_route" | "inside",
 *                 actual_timestamp?: number
 *               }
 *             }
 *           }
 *         }>
 *       }
 *     }
 *   }>,
 *   sliderPosition: { label: number } // epoch seconds used as the interpolation time
 * }} params
 *
 * @returns {Array<{
 *   points: {
 *     vesselName: string,
 *     routeType: string,
 *     geojson: {
 *       features: [ // exactly one feature
 *         {
 *           geometry: { coordinates: [number, number] },
 *           properties: any
 *         }
 *       ]
 *     }
 *   }
 * }>}
 */
// A route's identity for label lookup — its routeId, with an index fallback.
export function vesselRouteKey(route, idx = 0) {
  return route?.points?.routeId ?? route?.routeId ?? `idx-${idx}`
}

// Map(routeId -> permanent marker label). Same-vessel/same-state siblings (two
// suggested or two archived) get a discriminator (route name, else #1/#2); a
// lone route per status keeps the plain vessel name. Grouped by real state, so
// the Pathfinder candidate is not mixed in with saved routes.
export function buildVesselLabels(routeList) {
  const list = Array.isArray(routeList) ? routeList : []
  const keyOf = (route) =>
    `${route?.points?.vesselName ?? ""}|${
      route?.points?.state ?? route?.points?.routeType ?? ""
    }`

  const groups = new Map()
  list.forEach((route, idx) => {
    const k = keyOf(route)
    if (!groups.has(k)) groups.set(k, [])
    groups.get(k).push({ route, idx })
  })

  const labels = new Map()
  for (const members of groups.values()) {
    members.forEach(({ route, idx }, position) => {
      const vesselName = route?.points?.vesselName ?? "Vessel"
      if (members.length < 2) {
        labels.set(vesselRouteKey(route, idx), vesselName)
        return
      }
      const routeName = route?.points?.name?.trim?.()
      const discriminator = routeName || `#${position + 1}`
      labels.set(vesselRouteKey(route, idx), `${vesselName} · ${discriminator}`)
    })
  }
  return labels
}

export function buildVesselsGeojson({ routes, sliderPosition, labels }) {
  // Ohne Punkte oder ohne Zeiten (RTZ-Import ohne Zeitplan) gibt es kein Vessel.
  const routeList = (Array.isArray(routes) ? routes : []).filter((route) => {
    const features = route?.points?.geojson?.features ?? []
    return features.some((f) => f?.properties?.timestamp != null)
  })
  if (routeList.length === 0) return []

  // Caller-supplied label map (across all co-visible routes) or local fallback.
  const labelMap = labels ?? buildVesselLabels(routeList)

  let vesselGeojson = routeList.map((route, routeIndex) => {
    // const start = Date.now()

    // Look for vessel object in WPs
    let lastValidFeature = null
    for (let i = 0; i < route.points.geojson.features.length; i++) {
      if (
        route.points.geojson.features[i].properties.timestamp >
        sliderPosition.label
      ) {
        lastValidFeature = route.points.geojson.features[i]
        break
      }
    }
    // Set on the base object so it survives the immutable round-trip below.
    const vesselLabel =
      labelMap.get(vesselRouteKey(route, routeIndex)) ??
      route?.points?.vesselName
    let vesselPosition = {
      ...route.points,
      label: vesselLabel,
      geojson: {
        ...route.points.geojson,
        features: lastValidFeature
          ? [lastValidFeature]
          : route.points.geojson.features.slice(-1),
      },
    }
    // If route has < 2 WPs
    if (route.points.geojson.features.length < 2) {
      const [single] = vesselPosition.geojson.features
      vesselPosition.geojson.features = [structuredClone(single)]
      applyReportedSensorRotation(vesselPosition, route.points.geojson.features)
      return { points: vesselPosition }
    }
    // Interpolation for time slider
    let routePoints = RouteAnalyser.setTimeIntervalTree(fromJS(route.points))
    vesselPosition = RouteAnalyser.setVesselInterpolatePointByTime(
      fromJS(vesselPosition),
      routePoints,
      sliderPosition.label,
      "legtype",
    )

    // set correct timestamp if sliderPosition before or after route
    vesselPosition = vesselPosition.toJS()
    if (
      vesselPosition.geojson.features[0]?.properties?.analyse?.interpolate_data
        ?.intervaltree_match === "before_route"
    ) {
      const firstRouteFeatureTimestamp =
        route.points.geojson.features[0]?.properties?.timestamp
      vesselPosition.geojson.features[0].properties.analyse.interpolate_data.actual_timestamp =
        firstRouteFeatureTimestamp
    } else if (
      vesselPosition.geojson.features[0]?.properties?.analyse?.interpolate_data
        ?.intervaltree_match === "after_route"
    ) {
      const lastRouteFeatureTimestamp =
        route.points.geojson.features[route.points.geojson.features.length - 1]
          ?.properties?.timestamp
      vesselPosition.geojson.features[0].properties.analyse.interpolate_data.actual_timestamp =
        lastRouteFeatureTimestamp
    }
    vesselPosition = RouteAnalyser.setVesselBearing(
      fromJS(vesselPosition),
      routePoints,
      "legtype",
    ).toJS()
    applyReportedSensorRotation(vesselPosition, route.points.geojson.features)
    // Check if albis_reports is null in the final vesselPosition
    if (
      vesselPosition.routeType === "reported" &&
      vesselPosition.geojson.features[0].properties.albis_reports === null
    ) {
      // Loop through the route's features in reverse to find the last non-null albis_reports
      for (let i = route.points.geojson.features.length - 1; i >= 0; i--) {
        const feature = route.points.geojson.features[i]
        if (feature.properties.albis_reports !== null) {
          // Update the albis_reports in vesselPosition with the found non-null value
          vesselPosition.geojson.features[0].properties.albis_reports =
            feature.properties.albis_reports
          break // Exit the loop once the non-null albis_reports is found and assigned
        }
      }
    } else {
      return { points: vesselPosition }
    }

    return { points: vesselPosition }
  })
  return vesselGeojson
}

// Build VesselIcon-shaped objects from /vessels/last-positions nodes, so the
// latest position renders without loading the reported track. A finite heading
// rotates the icon (analyse.rhumbline_bearing); otherwise it falls back to the
// circle icon.
export function buildLastPositionVessels(lastPositions) {
  const result = []
  for (const p of lastPositions ?? []) {
    const lonlat = p?.lonlat
    if (!Array.isArray(lonlat) || lonlat.length !== 2) continue

    const rotation = resolveSensorRotation({ heading: p?.headingTrueDeg, sog: p?.sogKn })
    const analyse =
      rotation?.rotationDeg != null
        ? { rhumbline_bearing: rotation.rotationDeg, is_low_distance: false }
        : { is_low_distance: true }

    result.push({
      points: {
        vesselId: p?.vesselId ?? null,
        vesselName: p?.vesselName ?? null,
        routeType: "reported",
        state: null,
        label: p?.vesselName ?? null,
        geojson: {
          features: [
            {
              type: "Feature",
              geometry: { type: "Point", coordinates: [lonlat[0], lonlat[1]] },
              properties: {
                timestamp: p?.measuredAt ?? null,
                sog: p?.sogKn ?? null,
                heading: p?.headingTrueDeg ?? null,
                analyse,
              },
            },
          ],
        },
      },
    })
  }
  return result
}

/**
 * Return the tick nearest in the future relative to now (UTC).
 * Falls back to `fallbackTick` (usually your lastTick) if no future tick exists.
 *
 * @param {Array<{label:string|number}>} sliderTicks
 * @param {{label:string|number}|null} fallbackTick
 * @param {dayjs.Dayjs} [nowUTC=nowUtc()]
 * @returns {{label:string|number}|null}
 */
export function getNearestFutureTick(
  sliderTicks,
  fallbackTick,
  nowUTC = nowUtc(),
) {
  if (!Array.isArray(sliderTicks) || sliderTicks.length === 0)
    return fallbackTick ?? null

  // "jetzt" auf einem Tick zählt mit (Demo-Jetzt liegt exakt auf 00:00)
  const future = sliderTicks.filter((t) => !dayjs(t.label).isBefore(nowUTC))
  if (future.length === 0)
    return fallbackTick ?? sliderTicks[sliderTicks.length - 1]

  return future.reduce((prev, curr) =>
    dayjs(curr.label).diff(nowUTC) < dayjs(prev.label).diff(nowUTC)
      ? curr
      : prev,
  )
}

export function pointFeatureMs(feature) {
  const ts = feature?.properties?.timestamp
  if (!ts) return null
  const ms = dayjs.utc(ts).valueOf()
  return Number.isFinite(ms) ? ms : null
}

// Recenter target: last waypoint once the route is fully past atMs, else the
// waypoint nearest to atMs. Returns [lon, lat] or null.
export function vesselRecenterPoint(route, atMs = nowMs()) {
  const features = route?.points?.geojson?.features ?? []
  if (features.length === 0) return null

  const coordsOf = (feature) => {
    const c = feature?.geometry?.coordinates
    return Array.isArray(c) &&
      Number.isFinite(Number(c[0])) &&
      Number.isFinite(Number(c[1]))
      ? c
      : null
  }

  const timed = features
    .map((f) => ({ f, ms: pointFeatureMs(f) }))
    .filter((x) => x.ms != null)
  if (timed.length === 0) return coordsOf(features[features.length - 1])

  const lastMs = timed[timed.length - 1].ms
  if (atMs > lastMs) return coordsOf(features[features.length - 1])

  const nearest = timed.reduce((prev, curr) =>
    Math.abs(curr.ms - atMs) < Math.abs(prev.ms - atMs) ? curr : prev,
  )
  return coordsOf(nearest.f)
}

// Line segment timestamp: its end waypoint (wp2), falling back to start (wp1).
function lineFeatureMs(feature) {
  const wp2 = feature?.properties?.analyse?.wp2?.properties?.timestamp
  const wp1 = feature?.properties?.analyse?.wp1?.properties?.timestamp
  const ts = wp2 ?? wp1
  if (!ts) return null
  const ms = dayjs.utc(ts).valueOf()
  return Number.isFinite(ms) ? ms : null
}

// Clip route points and line segments to those at or before endMs (epoch ms).
// Unchanged routes pass through by identity; routes with no surviving point are
// dropped. Nullish endMs is a no-op.
export function clipRoutesToTimestamp(routes, endMs) {
  if (!Array.isArray(routes) || endMs == null) return routes
  const result = []
  for (const route of routes) {
    const pointFeatures = route?.points?.geojson?.features ?? []
    const lineFeatures = route?.lines?.geojson?.features ?? []

    const keptPoints = pointFeatures.filter((f) => {
      const ms = pointFeatureMs(f)
      return ms == null || ms <= endMs
    })
    const keptLines = lineFeatures.filter((f) => {
      const ms = lineFeatureMs(f)
      return ms == null || ms <= endMs
    })

    const pointsUnchanged = keptPoints.length === pointFeatures.length
    const linesUnchanged = keptLines.length === lineFeatures.length

    if (pointsUnchanged && linesUnchanged) {
      result.push(route)
      continue
    }
    if (keptPoints.length === 0) continue

    result.push({
      ...route,
      points: {
        ...route.points,
        geojson: { ...route.points.geojson, features: keptPoints },
      },
      lines: route.lines
        ? {
            ...route.lines,
            geojson: { ...route.lines.geojson, features: keptLines },
          }
        : route.lines,
    })
  }
  return result
}

// Append a vessel's last reported position as an extra waypoint (+ connecting
// line segment) to its reported track, but only when it is fresh and newer than
// the track's last waypoint. Keeps line and marker on the most recent point
// instead of drawing the last position as a separate marker.
export function appendLastPositionsToRoutes(routes, lastPositions) {
  if (!Array.isArray(routes) || !Array.isArray(lastPositions) || lastPositions.length === 0) {
    return routes
  }
  const posByName = new Map()
  for (const p of lastPositions) {
    if (p?.vesselName != null && !p?.isStale) posByName.set(p.vesselName, p)
  }
  if (posByName.size === 0) return routes

  return routes.map((route) => {
    const pos = posByName.get(route?.points?.vesselName)
    if (!pos || !Array.isArray(pos.lonlat)) return route

    const feats = route?.points?.geojson?.features ?? []
    if (feats.length === 0) return route

    const posMs = pos.measuredAt ? dayjs.utc(pos.measuredAt).valueOf() : NaN
    if (!Number.isFinite(posMs)) return route

    let lastMs = -Infinity
    let lastFeat = null
    for (const f of feats) {
      const ms = pointFeatureMs(f)
      if (ms != null && ms > lastMs) {
        lastMs = ms
        lastFeat = f
      }
    }
    if (lastFeat == null || posMs <= lastMs) return route

    const coords = [pos.lonlat[0], pos.lonlat[1]]
    const newPoint = {
      type: "Feature",
      geometry: { type: "Point", coordinates: coords },
      properties: {
        timestamp: pos.measuredAt,
        sog: pos.sogKn ?? null,
        heading: pos.headingTrueDeg ?? null,
        legtype: "rhumbline",
        is_last_position: true,
        analyse: { is_gap_time_by_timestamps: false },
      },
    }
    const newLine = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [lastFeat.geometry.coordinates, coords],
      },
      properties: { analyse: { wp1: lastFeat, wp2: newPoint } },
    }

    return {
      ...route,
      points: {
        ...route.points,
        geojson: { ...route.points.geojson, features: [...feats, newPoint] },
      },
      lines: route.lines
        ? {
            ...route.lines,
            geojson: {
              ...route.lines.geojson,
              features: [...(route.lines.geojson?.features ?? []), newLine],
            },
          }
        : route.lines,
    }
  })
}
